import { useEffect, useRef } from 'react';
import type { Category } from '../types';
import { legibleOn } from '../utils/legibleOn';
import { STOREFRONT_CATEGORY_IDS } from '../utils/amenities';

// Fixed-color overrides via CSS variable references with hex fallbacks.
// Amenity types mirror AMENITY_FILLS in utils/amenities.ts. The
// storefront overrides reuse the slate token so brick-and-mortar
// vendors render identically regardless of what color the Categories
// sheet specifies for that category. These rules come AFTER the
// per-category rules so they win the cascade.
const SLATE_RULE = (id: string) =>
  `[data-cat-id="${id}"] { --cat-color: var(--palette-slate, #76849A); --cat-fg: var(--color-text-on-dark, #F0E8D6); }`;

const FIXED_RULES = [
  '[data-cat-id="atm"] { --cat-color: var(--palette-amenity-money, #2d6a4f); --cat-fg: var(--color-text-on-dark, #F0E8D6); }',
  '[data-cat-id="first_aid"] { --cat-color: var(--palette-category-red, #b04838); --cat-fg: var(--color-text-on-dark, #F0E8D6); }',
  '[data-cat-id="restroom"] { --cat-color: var(--palette-slate, #76849A); --cat-fg: var(--color-text-on-dark, #F0E8D6); }',
  '[data-cat-id="stage"] { --cat-color: var(--palette-amenity-stage, #906030); --cat-fg: var(--color-text-on-dark, #F0E8D6); }',
  ...[...STOREFRONT_CATEGORY_IDS].map(SLATE_RULE),
].join('\n');

function buildCSS(categories: Category[]): string {
  const catRules = categories
    .map(cat => {
      const escaped = CSS.escape(cat.id);
      const fg = legibleOn(cat.color);
      return `[data-cat-id="${escaped}"] { --cat-color: ${cat.color}; --cat-fg: ${fg}; }`;
    })
    .join('\n');
  return `${catRules}\n${FIXED_RULES}`;
}

/**
 * Maintains a Constructable Stylesheet on `document.adoptedStyleSheets`
 * that maps `[data-cat-id="<id>"]` selectors to `--cat-color` and
 * `--cat-fg` custom properties. Components use `data-cat-id={cat.id}`
 * instead of `style={{ '--cat-color': cat.color }}`, keeping all style
 * mutations out of inline style attributes and inside CSS rules where
 * `style-src 'self'` covers them without needing `'unsafe-inline'`.
 */
export function useCategoryStyleSheet(categories: Category[]): void {
  const sheetRef = useRef<CSSStyleSheet | null>(null);

  useEffect(() => {
    if (typeof document === 'undefined' || !('adoptedStyleSheets' in document)) return;

    if (!sheetRef.current) {
      sheetRef.current = new CSSStyleSheet();
      document.adoptedStyleSheets = [...document.adoptedStyleSheets, sheetRef.current];
    }

    sheetRef.current.replaceSync(buildCSS(categories));

    return () => {
      const sheet = sheetRef.current;
      if (!sheet) return;
      document.adoptedStyleSheets = document.adoptedStyleSheets.filter(s => s !== sheet);
      sheetRef.current = null;
    };
  }, [categories]);
}
